import { useEffect, useMemo, useState } from 'react';

import { padColumns } from 'utils/pad-columns';

import { PayoutDetails } from '@pages/Payouts/Details/PayoutDetails';
import {
  type GetDisbursementPayoutRequestsQueryError,
  type GetDisbursementPayoutRequestsQueryResult,
  type PayoutRequestItem,
  useGetDisbursementPayoutRequests
} from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';

import { getColumns } from 'components/pages/Payouts/columns';

import { PAYOUTS_SEARCH_RESULTS_TEST_ID } from './constants';

import { SearchResults } from '../SearchResults';

type Props = {
  onSuccess: (payout: PayoutRequestItem | undefined) => void;
  searchParams: any;
};

export const PayoutsSearchResults = ({ searchParams, onSuccess }: Props) => {
  const organisationId = useOrganisationId<number>();

  const { data, error, isFetching, isSuccess, refetch } =
    useGetDisbursementPayoutRequests<
      GetDisbursementPayoutRequestsQueryResult,
      GetDisbursementPayoutRequestsQueryError
    >(organisationId, searchParams);

  useEffect(() => {
    if (!isFetching && isSuccess) {
      onSuccess(data?.data[0]);
    }
  }, [isFetching, isSuccess, data, onSuccess]);

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const payoutRequestColumns = useMemo(
    () => padColumns(getColumns(findAccountByAccountNumber)),
    [findAccountByAccountNumber]
  );

  const handleRowClick = (record: PayoutRequestItem) => {
    setSelectedRowData((selected) =>
      selected !== record ? record : undefined
    );
  };

  const [selectedRowData, setSelectedRowData] = useState<
    PayoutRequestItem | undefined
  >(undefined);

  useEffect(() => {
    setSelectedRowData(data?.data?.[0]);
  }, [data]);

  if (Boolean(data?.data.length) && !error) {
    return (
      <SearchResults
        title="Payout requests"
        idKey="id"
        detailComponent={
          selectedRowData?.unique_reference ? (
            <PayoutDetails
              onClose={() => setSelectedRowData(undefined)}
              uniqueReferenceId={selectedRowData?.unique_reference}
              onTestCaseSuccess={refetch}
            />
          ) : undefined
        }
        data={data?.data ?? []}
        e2eId={PAYOUTS_SEARCH_RESULTS_TEST_ID}
        isFetching={isFetching}
        columnsDefinition={payoutRequestColumns}
        onRowClick={handleRowClick}
        isSelected={(record) => record.id === selectedRowData?.id}
      />
    );
  }
};
